import { useState } from "react"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import LicenseCreateDialog, {
  type LicenseCreateBridge,
} from "@/v2-backoffice-client/license/create/LicenseCreateDialog"
import { useLicenseGoverningStatuses } from "@/v2-backoffice-client/license/license.statuses.hooks"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseBridgeMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// The one lever on a billing-derived license that is not live (Scheduled,
// Expired, Not starting): nobody is let in, billing owns the row so nothing
// on it can be edited, and a plain grant used to be refused because the slot
// is taken. The bridge is a trial granted over the same slot — the one
// operator row the hourly sync leaves alone — so people are let in until
// billing is live; the grant dialog does the rest, preset to the slot.
function LicenseBridgeMenuItem(props: LicenseBridgeMenuItemProps) {
  const { license, children, ...menuItemProps } = props
  const [isOpen, setIsOpen] = useState(false)
  const { namesByCode } = useLicenseGoverningStatuses()

  const bridge: LicenseCreateBridge = {
    productCode: license.ProductCode,
    productName: license.ProductName,
    seatsAreAssigned: license.SeatsAreAssigned,
    planId: license.PlanId ?? null,
    statusName: namesByCode.get(license.LicenseStatusCode) ?? "not live",
  }

  return (
    <>
      <ActionMenu.Item
        iconSrc="life-ring"
        data-feature-code="UI_BackofficeLicense_Row_Bridge"
        onSelect={(e) => {
          e.preventDefault()
          setIsOpen(true)
        }}
        {...menuItemProps}
      >
        {children ?? "Bridge With a Trial"}
      </ActionMenu.Item>

      <LicenseCreateDialog
        customerId={license.CustomerId}
        customerName={license.CustomerName ?? undefined}
        bridge={bridge}
        open={isOpen}
        onOpenChange={setIsOpen}
      />
    </>
  )
}

export default LicenseBridgeMenuItem
